/**
 * ContactPage — /contact (V12.x)
 *
 * Formulaire de contact simple qui ouvre le client mail par défaut via
 * `mailto:` vers l'email admin configuré (site_settings.admin_contact.email,
 * défaut : anna.yyc@gmail.com — modifiable dans Admin › Site › Contact admin).
 */
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, Send, MapPin, Sparkles, ShieldCheck } from "lucide-react";
import { adminMailto, getAdminEmail } from "@/lib/adminContact";

const SUBJECT_PRESETS = [
  { key: "general", label: "Question générale" },
  { key: "athlete", label: "Ma fiche athlète" },
  { key: "coach", label: "Espace coach / club" },
  { key: "federation", label: "Espace fédération" },
  { key: "signal", label: "Signaler un contenu" },
  { key: "archive", label: "Proposer une archive Mémoire" },
  { key: "partnership", label: "Partenariat / sponsor" },
  { key: "bug", label: "Bug technique" },
];

export default function ContactPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("Question générale");
  const [message, setMessage] = useState("");

  const canSend = message.trim().length > 0;
  const composeMailto = () => {
    const body = [
      name && `De : ${name}`,
      email && `Email de retour : ${email}`,
      "",
      message,
    ].filter(Boolean).join("\n");
    return adminMailto({
      subject: `[AITO.PF] ${subject}`,
      body,
    });
  };

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8" data-testid="contact-page">
      <Badge className="bg-[hsl(var(--aito-lagoon))]/10 text-[hsl(var(--aito-lagoon))] border-[hsl(var(--aito-lagoon))]/30 mb-3">Contact</Badge>
      <h1 className="font-display font-bold text-3xl sm:text-4xl leading-tight">Nous contacter</h1>
      <p className="mt-2 text-muted-foreground">
        Une question, un signalement, une archive à partager, une demande de partenariat ? Un seul point de contact,
        l'équipe AITO.PF répond dans les meilleurs délais.
      </p>

      <Card className="mt-6 p-5 sm:p-6 space-y-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Mail className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
          <span>Écrivez à <a href={adminMailto()} className="font-semibold text-[hsl(var(--aito-lagoon))] hover:underline">{getAdminEmail()}</a></span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="c-name">Votre nom</Label>
            <Input id="c-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Prénom Nom" data-testid="contact-name" />
          </div>
          <div>
            <Label htmlFor="c-email">Votre email (facultatif)</Label>
            <Input id="c-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@exemple.com" data-testid="contact-email" />
          </div>
        </div>
        <div>
          <Label htmlFor="c-subject">Sujet</Label>
          <select
            id="c-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            data-testid="contact-subject"
            className="mt-1 w-full h-10 rounded-md border bg-background px-3 text-sm"
          >
            {SUBJECT_PRESETS.map((s) => <option key={s.key} value={s.label}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <Label htmlFor="c-message">Message *</Label>
          <Textarea
            id="c-message"
            rows={7}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Bonjour, ..."
            data-testid="contact-message"
          />
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-2 border-t">
          <p className="text-[11px] text-muted-foreground max-w-md leading-snug">
            <ShieldCheck className="inline h-3 w-3 mr-1 text-[hsl(var(--aito-lagoon))]" />
            Le formulaire ouvre votre client mail avec le message pré-rempli.
            Aucune donnée n'est stockée sur AITO.PF.
          </p>
          <Button asChild disabled={!canSend} className="bg-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon))]/90 text-white" data-testid="contact-submit">
            <a href={canSend ? composeMailto() : "#"}>
              <Send className="h-4 w-4 mr-1.5" /> Envoyer par email
            </a>
          </Button>
        </div>
      </Card>

      <Card className="mt-4 p-5 bg-secondary/40 border-dashed">
        <h2 className="font-semibold text-sm flex items-center gap-2"><Sparkles className="h-4 w-4 text-[hsl(var(--aito-gold))]" /> Vous êtes admin ?</h2>
        <p className="text-xs text-muted-foreground mt-1">
          L'email de contact affiché ici est <strong>{getAdminEmail()}</strong>. Il est modifiable
          depuis <a href="/admin/site-settings" className="underline text-[hsl(var(--aito-lagoon))]">Admin › Site › Contact admin</a>.
        </p>
      </Card>
    </div>
  );
}
